import { ObjectTools } from '../../tools/object-tools';
import { SolIcon } from '@sol/icons';
import { SolButton, SolButtonType } from './sol-button';
import { SolText } from './sol-text';
import { SolTextBox } from './sol-text-box';

export class SolEmptyState {
  private constructor(
    public readonly title: SolText,
    public readonly text: SolTextBox | undefined,
    public readonly icon: SolIcon | undefined,
    public readonly customContent: boolean,
    public readonly hasText: boolean,
    public readonly hasAction: boolean,
    public readonly hasIcon: boolean,
    public readonly hasCustomContent: boolean,
    public readonly actionButton: SolButton | undefined,
    public readonly id: string | undefined,
  ) {}

  public static of(
    title: SolText = SolText.empty(),
    text: SolText | undefined = undefined,
    icon: SolIcon | undefined = undefined,
    customContent: boolean = false,
  ): SolEmptyState {
    const emptyState = new SolEmptyState(
      title,
      SolEmptyState.toTextBox(text),
      icon,
      customContent,
      false,
      false,
      false,
      false,
      undefined,
      undefined,
    );
    return emptyState.calculate();
  }

  public static ofNoResults(title?: SolText, text?: SolText): SolEmptyState {
    return SolEmptyState.of(
      title ?? SolText.of('sol-empty-state.no-results-title'),
      text ?? SolText.of('sol-empty-state.no-results-text'),
    ).withId('no-results');
  }

  public static ofError(text?: SolText): SolEmptyState {
    return SolEmptyState.of(SolText.of('sol-empty-state.error-title'), text ?? SolText.of('sol-empty-state.error-text'))
      .withIcon(SolIcon.warning)
      .withId('error');
  }

  public static ofForbidden(text?: SolText): SolEmptyState {
    return SolEmptyState.of(
      SolText.of('sol-empty-state.forbidden-title'),
      text ?? SolText.of('sol-empty-state.forbidden-text'),
    )
      .withIcon(SolIcon.lock)
      .withId('forbidden');
  }

  public withId(id: string): SolEmptyState {
    return this.copy({ id });
  }

  public withTitle(title: SolText): SolEmptyState {
    return this.copy({ title }).calculate();
  }

  public withText(text: SolText | undefined): SolEmptyState {
    return this.copy({ text: SolEmptyState.normalizeTextBox(text) }).calculate();
  }

  public withActionButton(actionButton: SolButton | undefined): SolEmptyState {
    actionButton = actionButton?.withType(SolButtonType.link);
    return this.copy({ actionButton }).calculate();
  }

  public withIcon(icon: SolIcon | undefined): SolEmptyState {
    return this.copy({ icon }).calculate();
  }

  public withCustomContent(customContent: boolean): SolEmptyState {
    return this.copy({ customContent }).calculate();
  }

  private calculate(): SolEmptyState {
    const hasText = this.text !== undefined;
    const hasAction = this.actionButton !== undefined;
    const hasIcon = this.icon !== undefined;
    const hasCustomContent = this.customContent;
    return this.copy({
      hasText,
      hasAction,
      hasIcon,
      hasCustomContent,
    });
  }

  private copy(props: Partial<SolEmptyState>): SolEmptyState {
    return ObjectTools.copy<SolEmptyState>(this, props);
  }

  private static toTextBox(text: SolText | undefined): SolTextBox | undefined {
    return text === undefined ? undefined : SolTextBox.of(text).withCollapsedHeight('64').withCentered(true);
  }

  private static normalizeTextBox(text: SolText | undefined): SolTextBox | undefined {
    if (text === undefined) {
      return undefined;
    }
    return text.isEmpty() ? undefined : SolTextBox.of(text);
  }
}
